import { useState, useRef } from 'react';
import { CloudArrowUpIcon, ArrowLeftIcon, BuildingLibraryIcon } from '@heroicons/react/24/outline';

const StatementUpload = ({ onUpload, onCancel, isProcessing, accounts = null, currentAccount = null }) => {
  const [dragActive, setDragActive] = useState(false);
  const [selectedAccountId, setSelectedAccountId] = useState(currentAccount?._id || 'auto-detect');
  const inputRef = useRef(null);
  
  // Show account selector if accounts are provided and there's more than one
  const showAccountSelector = accounts && accounts.length > 1;

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      onUpload(e.dataTransfer.files[0], selectedAccountId);
    }
  };

  const handleChange = (e) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      onUpload(e.target.files[0], selectedAccountId);
    }
  };

  return (
    <div className="bg-white rounded-xl p-6">
      <button
        onClick={onCancel}
        className="mb-4 inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-lg bg-indigo-100 text-indigo-600 hover:bg-indigo-200 transition-colors duration-200"
      >
        <ArrowLeftIcon className="h-4 w-4 mr-2" />
        Back to options
      </button>

      {/* Account Selector (optional) */}
      {showAccountSelector && (
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Which account is this statement for?
          </label>
          <div className="relative">
            <select
              value={selectedAccountId}
              onChange={(e) => setSelectedAccountId(e.target.value)}
              className="block w-full px-4 py-2 pr-10 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 appearance-none bg-white"
            >
              <option value="auto-detect">🔍 Auto-detect from statement</option>
              {accounts.map((account) => (
                <option key={account._id} value={account._id}>
                  {account.institutionName || account.bankName || 'Unknown'} - {account.name || account.accountType} 
                  {account.mask && ` (••••${account.mask})`}
                </option>
              ))}
            </select>
            <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
              <BuildingLibraryIcon className="h-5 w-5 text-gray-400" />
            </div>
          </div>
          <p className="mt-1 text-xs text-gray-500">
            Select an account to help us match this statement, or let us auto-detect it.
          </p>
        </div>
      )}

      <div 
        className={`border-2 border-dashed rounded-xl p-8 text-center relative ${
          dragActive ? 'border-indigo-500 bg-indigo-50' : 'border-gray-300'
        }`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <input
          ref={inputRef}
          type="file"
          accept=".pdf"
          onChange={handleChange}
          className="hidden"
        />

        <CloudArrowUpIcon className="mx-auto h-12 w-12 text-indigo-500 mb-4" />
        
        <button
          onClick={() => inputRef.current.click()}
          disabled={isProcessing}
          className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg bg-indigo-500 text-white hover:bg-indigo-600 disabled:bg-indigo-300 transition-colors"
        >
          Choose a PDF file
        </button>

        <p className="mt-2 text-sm text-gray-500">
          or drag and drop
        </p>

        {isProcessing && (
          <div className="absolute inset-0 bg-white/80 flex items-center justify-center rounded-xl">
            <div className="flex items-center space-x-2">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-indigo-500"></div>
              <span className="text-sm text-gray-600">Processing...</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StatementUpload; 